import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface SafetyIssueSummaryProps {
  openCount: number;
  openIssuesPath: string;
  resolvedCount: number;
  issues: Array<{
    location_detail: { name: string } | null;
  }>;
}

interface ChartSegment {
  color: string;
  label: string;
  value: number;
}

interface AreaSummary {
  count: number;
  name: string;
}

const STATUS_COLORS = {
  open: '#ef4444',
  resolved: '#22c55e',
};

const AREA_BAR_COLORS = ['bg-blue-500', 'bg-sky-500', 'bg-indigo-500', 'bg-slate-400'];

const summarizeAreas = (issues: SafetyIssueSummaryProps['issues']): AreaSummary[] => {
  const counts = new Map<string, number>();
  issues.forEach((issue) => {
    const name = issue.location_detail?.name.trim() || 'Unassigned';
    counts.set(name, (counts.get(name) ?? 0) + 1);
  });

  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    .slice(0, 4);
};

const DonutChart = ({
  segments,
  size = 96,
  strokeWidth = 14,
}: {
  segments: ChartSegment[];
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total === 0) {
    return (
      <svg role="img" aria-label="No safety issues" width={size} height={size} className="mx-auto">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  let offset = 0;
  const arcs = segments.map((segment) => {
    const dash = (segment.value / total) * circumference;
    const currentOffset = offset;
    offset += dash;
    return (
      <circle
        key={segment.label}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={segment.color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={-currentOffset}
        strokeLinecap="butt"
      />
    );
  });

  return (
    <svg
      role="img"
      aria-label={segments.map((segment) => `${segment.label}: ${segment.value}`).join(', ')}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="geometricPrecision"
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>{arcs}</g>
    </svg>
  );
};

const GaugeChart = ({
  percent,
  size = 64,
  strokeWidth = 9,
}: {
  percent: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (percent / 100) * circumference;

  return (
    <svg
      role="img"
      aria-label={`${percent.toFixed(1)} percent of safety issues resolved`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="geometricPrecision"
    >
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={STATUS_COLORS.resolved}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

export const SafetyIssueSummary = ({
  openCount,
  openIssuesPath,
  resolvedCount,
  issues,
}: SafetyIssueSummaryProps) => {
  const totalCount = openCount + resolvedCount;
  const resolvedPercent = totalCount === 0 ? 0 : (resolvedCount / totalCount) * 100;
  const statusSegments: ChartSegment[] = [
    { label: 'Open', value: openCount, color: STATUS_COLORS.open },
    { label: 'Resolved', value: resolvedCount, color: STATUS_COLORS.resolved },
  ];
  const areaSummary = summarizeAreas(issues);
  const maxAreaCount = Math.max(...areaSummary.map((area) => area.count), 1);
  const hasOpenIssues = openCount > 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card
        role="status"
        aria-label={
          hasOpenIssues
            ? `${openCount} open safety ${openCount === 1 ? 'issue' : 'issues'} need attention`
            : 'No open safety issues; all clear'
        }
        className={
          hasOpenIssues
            ? 'border-destructive/40 bg-destructive/5'
            : 'border-success/40 bg-success/5'
        }
      >
        <CardHeader className="pb-2">
          <CardTitle
            className={`text-xs font-semibold ${hasOpenIssues ? 'text-destructive' : 'text-success'}`}
          >
            Open Issues
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center gap-2 py-3">
          <span
            className={`text-4xl font-bold tabular-nums ${hasOpenIssues ? 'text-destructive' : 'text-success'}`}
          >
            {openCount}
          </span>
          <span
            className={`text-xs font-semibold ${hasOpenIssues ? 'text-destructive' : 'text-success'}`}
          >
            {hasOpenIssues ? 'Needs attention' : 'All clear'}
          </span>
          <Button asChild size="sm" variant={hasOpenIssues ? 'destructive' : 'secondary'}>
            <Link to={openIssuesPath}>
              See Issues
              <ArrowRight />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            Status Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          <DonutChart segments={statusSegments} />
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
            {statusSegments.map((segment) => (
              <span
                key={segment.label}
                className="flex items-center gap-1 text-[11px] text-muted-foreground"
              >
                <span
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                {segment.label} ({segment.value})
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            Issues by Area
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col justify-center gap-2.5 py-3">
          {areaSummary.length === 0 ? (
            <div className="py-4 text-xs text-muted-foreground">No issues to break down yet.</div>
          ) : (
            areaSummary.map((area, index) => (
              <div key={area.name} className="flex items-center gap-2 text-[11px]">
                <span className="w-20 shrink-0 truncate text-muted-foreground">{area.name}</span>
                <div className="relative h-2.5 flex-1 rounded-full bg-muted">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full ${AREA_BAR_COLORS[index] ?? 'bg-primary'}`}
                    style={{ width: `${(area.count / maxAreaCount) * 100}%` }}
                  />
                </div>
                <span className="w-5 text-right font-semibold tabular-nums">{area.count}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">Resolved Rate</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center gap-1 py-3">
          <div className="flex items-center gap-3">
            <GaugeChart percent={resolvedPercent} />
            <span className="text-3xl font-bold tabular-nums">{resolvedPercent.toFixed(1)}%</span>
          </div>
          <span className="text-xs text-muted-foreground">
            across {totalCount} safety issue{totalCount === 1 ? '' : 's'}
          </span>
        </CardContent>
      </Card>
    </div>
  );
};
